import React, { useState, useEffect, useRef } from "react";
import { Button, Form } from "reactstrap";
import { Modal } from "react-bootstrap";
import axiosConn from "config/axiosConn";
import { useNavigate } from "react-router-dom";
import VirtualKeyboard from "./SchoolAdmin/VirtualKeyboard/VirtualKeyboard";
import * as Yup from "yup";
import Langfield from "../assets/Json/accountFormLang.json";
import {
  handleSpeak,
  handleSpeakWithoutVoice,
} from "./SchoolAdmin/speechHandlers";
import { handleFocusChange, skipFocusChange } from "./SchoolAdmin/formUtils";
import InputField from "./SchoolAdmin/InputField";
import InputField2 from "./SchoolAdmin/InputField12";
import "react-toastify/dist/ReactToastify.css";
import { useSpeechRecognition } from "react-speech-recognition";
import { Formik, Field, ErrorMessage } from "formik";
import { toast } from "react-toastify";
import isEmail from "validator/lib/isEmail";

function CollaborationModel(props) {
  const navigate = useNavigate();
  const [langSelected, setLangSelected] = useState("en-IN");
  const [check, setCheck] = useState(false);
  const [fileName, setFileName] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [voiceSelected, setVoiceSelected] = useState("en-IN-NeerjaNeural");
  const [initialValues, setInitialValues] = useState({});

  const disableRef = useRef(false);
  const nextValueRef = useRef(0);
  const resetRef = useRef(0);
  const onBtnRef = useRef(null);
  const isKeyBoardInput = useRef(false);
  const { listening, transcript, resetTranscript } = useSpeechRecognition();
  const translations = Langfield[langSelected];
  const translate = (key) => {
    // Check if translations is defined and is an object
    if (typeof translations !== "object" || translations === null) {
      return key; // Return the key if translations is not available
    }

    // Handle undefined or non-string keys
    if (typeof key !== "string") {
      return key; // Return the value as is if key is not a string
    }

    return translations[key] || key.replace(/_/g, " "); // Use translation or fallback to the key
  };

  const facultyRef = useRef(null);
  const emailRef = useRef(null);
  const numberRef = useRef(null);
  const commentRef = useRef(null);
  const getActiveState = useRef("contact_person");
  const [getActive, setActive] = useState("");

  const nextRef = [facultyRef, emailRef, numberRef, commentRef];
  let timer = null;

  useEffect(() => {
    handleFocusChange(nextRef, nextValueRef, resetRef, timer);
  }, []);

  const handleActive = (e) => {
    const elementId = e.target.id;
    setActive(elementId);
  };

  const handleImageUpload = (e) => {
    try {
      const allowedExtensions = ["png", "jpeg", "jpg"];
      // CRITICAL: Read files synchronously IMMEDIATELY before any async operations
      const files = Array.from(e.target.files);

      if (files.length > 5) {
        toast.warning("You can only upload up to 5 files at a time");
        return;
      }

      // Filter files synchronously
      const validFiles = [];
      for (let i = 0; i < files.length; i++) {
        const fileExtension = files[i].type.split("/")[1];
        if (allowedExtensions.includes(fileExtension)) {
          validFiles.push(files[i]);
        } else {
          toast.warning("Only Image files (PNG, JPEG, JPG) are allowed");
          return;
        }
      }

      if (validFiles.length === 0) {
        return;
      }

      // NOW do the async upload
      uploadFilesAsync(validFiles);
    } catch (error) {
      console.error("Error in file selection:", error);
      toast.error("Error selecting files");
    }
  };

  const uploadFilesAsync = async (validFiles) => {
    try {
      const formData = new FormData();
      for (let file of validFiles) {
        formData.append("files", file);
      }

      const response = await axiosConn(
        "post",
        "upload-collaboration-file",
        formData
      );

      if (response.data.status === "success") {
        const uploadedFiles = response.data.data;
        setFileName(uploadedFiles); // Keep track of the file names
        toast.success("Files uploaded successfully");
      } else {
        toast.warning("Failed to upload files");
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      toast.error("An error occurred during file upload");
    }
  };

  const InputFields = [
    {
      name: "contact_person",
      label: translate("Contact_Person"),
      type: "text",
      required: true,
      placeholder: translate("contact_person"),
      innerRef: facultyRef,
      handleSpeak: (label) =>
        handleSpeak(
          label,
          langSelected,
          voiceSelected,
          getActiveState,
          onBtnRef,
          translate("Contact_Person")
        ),
    },
    {
      name: "contact_email",
      label: translate("Contact_Email"),
      type: "email",
      required: true,
      placeholder: translate("contact_email"),
      innerRef: emailRef,
      handleSpeak: (label) =>
        handleSpeak(
          label,
          langSelected,
          voiceSelected,
          getActiveState,
          onBtnRef,
          translate("Contact_Email")
        ),
    },
    {
      name: "contact_number",
      label: translate("Contact_Number"),
      type: "number",
      required: true,
      placeholder: translate("Contact_Number"),
      innerRef: numberRef,
      handleSpeak: (label) =>
        handleSpeak(
          label,
          langSelected,
          voiceSelected,
          getActiveState,
          onBtnRef,
          translate("Contact_Number")
        ),
    },
    {
      name: "comment",
      label: translate("Comment"),
      type: "textarea3",
      required: true,
      placeholder: translate("Comment"),
      innerRef: commentRef,
      handleSpeak: (label) =>
        handleSpeak(
          label,
          langSelected,
          voiceSelected,
          getActiveState,
          onBtnRef,
          translate("Comment")
        ),
    },
  ];

  const renderInputFields = (fieldConfig, setFieldValue, size = 4) => {
    return fieldConfig.type === "select" ? (
      <div
        key={fieldConfig.name}
        onFocus={(e) => {
          handleActive(e);
        }}
        className={`col-4 form-group ${
          getActive === fieldConfig.name ? "active" : null
        } `}
      >
        <label htmlFor={fieldConfig.name}>{fieldConfig.label}</label>
        <div className="formgroupIn">
          <Field
            onFocus={(e) => {
              getActiveState.current = fieldConfig.name;
              handleActive(e);
              handleSpeakWithoutVoice(
                fieldConfig.name,
                langSelected,
                voiceSelected,
                getActiveState,
                fieldConfig.label
              );
            }}
            innerRef={fieldConfig.innerRef}
            as="select"
            className="form-control"
            id={fieldConfig.name}
            name={fieldConfig.name}
            value={fieldConfig.value}
            disabled={
              (disableRef.current === true
                ? getActive === fieldConfig.name
                  ? false
                  : false
                : false) || fieldConfig.disabled
            }
            onChange={(e) => {
              setInitialValues({
                ...initialValues,
                [fieldConfig.name]: e.target.value,
              });
              handleFocusChange(nextRef, nextValueRef, resetRef, timer);
            }}
          >
            {fieldConfig.selectOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {translate(option.label)}
              </option>
            ))}
          </Field>
        </div>
      </div>
    ) : fieldConfig.type === "textarea" ? (
      <InputField2
        key={fieldConfig.name}
        name={fieldConfig.name}
        value={initialValues[fieldConfig.name]}
        disabled={fieldConfig.disabled}
        label={fieldConfig.label}
        setInitialValues={setInitialValues}
        // saveField={saveField}
        placeholder={fieldConfig.placeholder}
        innerRef={fieldConfig.innerRef}
        handleSpeak={fieldConfig.handleSpeak}
        handleFocusChange={handleFocusChange}
        nextRef={nextRef}
        nextValueRef={nextValueRef}
        resetRef={resetRef}
        timer={timer}
        setFieldValue={setFieldValue}
        getActive={getActive}
        handleActive={handleActive}
        langSelected={langSelected}
        getActiveState={getActiveState}
        onBtnRef={onBtnRef}
        skipFocusChange={skipFocusChange}
        skipBtn={translate("Skip")}
        transcript={transcript}
        voiceSelected={voiceSelected}
        listening={listening}
        resetTranscript={resetTranscript}
        required={fieldConfig.required}
        isKeyBoardInput={isKeyBoardInput}
        handleSpeakWithoutVoice={handleSpeakWithoutVoice}
        disableRef={disableRef}
        requiredBtn={translate("Required")}
      />
    ) : fieldConfig.type === "file" ? (
      <div
        onFocus={(e) => handleActive(e)}
        className={` col-4  form-group ${
          getActive === "file" ? "active" : null
        }`}
      >
        <label htmlFor="">
          {translate(
            "Bank/Branch_to_affix_rubber_stamp_of_name_and_code_number"
          )}
        </label>

        <div className="formgroupIn">
          <Field
            name="file"
            type="file"
            id="file"
            className="form-control"
            disabled={
              (disableRef.current === true
                ? getActive === fieldConfig.name
                  ? false
                  : true
                : false) || fieldConfig.disabled
            }
          />
        </div>
        <ErrorMessage name="file" component="div" />
      </div>
    ) : fieldConfig.type === "text" ? (
      <InputField
        initialValues={initialValues}
        setInitialValues={setInitialValues}
        size={size}
        // saveField={saveField}
        key={fieldConfig.name}
        name={fieldConfig.name}
        value={initialValues[fieldConfig.name]}
        disabled={fieldConfig.disabled}
        label={fieldConfig.label}
        placeholder={fieldConfig.placeholder}
        innerRef={fieldConfig.innerRef}
        handleSpeak={fieldConfig.handleSpeak}
        handleFocusChange={handleFocusChange}
        nextRef={nextRef}
        nextValueRef={nextValueRef}
        resetRef={resetRef}
        timer={timer}
        setFieldValue={setFieldValue}
        getActive={getActive}
        handleActive={handleActive}
        langSelected={langSelected}
        getActiveState={getActiveState}
        onBtnRef={onBtnRef}
        skipFocusChange={skipFocusChange}
        skipBtn={translate("Skip")}
        transcript={transcript}
        listening={listening}
        voiceSelected={voiceSelected}
        resetTranscript={resetTranscript}
        required={fieldConfig.required}
        isKeyBoardInput={isKeyBoardInput}
        handleSpeakWithoutVoice={handleSpeakWithoutVoice}
        disableRef={disableRef}
        requiredBtn={translate("Required")}
      />
    ) : fieldConfig.disabled ? (
      <div
        onFocus={(e) => handleActive(e)}
        className={` col-6  form-group ${
          getActive === fieldConfig.name ? "active" : null
        }`}
      >
        <label htmlFor={fieldConfig.name}>{fieldConfig.label}</label>
        <div className="formgroupIn">
          <Field
            onFocus={(e) => {
              getActiveState.current = fieldConfig.name;
              handleActive(e);
              handleSpeakWithoutVoice(
                fieldConfig.name,
                langSelected,
                voiceSelected,
                getActiveState,
                fieldConfig.label
              );
            }}
            innerRef={fieldConfig.innerRef}
            type={fieldConfig.type}
            className="form-control"
            id={fieldConfig.name}
            name={fieldConfig.name}
            placeholder={fieldConfig.placeholder}
            disabled={fieldConfig.disabled}
          />
          <span className="inptIcn"></span>
        </div>
      </div>
    ) : (
      <InputField
        initialValues={initialValues}
        size={size}
        setInitialValues={setInitialValues}
        // saveField={saveField}
        key={fieldConfig.name}
        name={fieldConfig.name}
        value={initialValues[fieldConfig.name]}
        disabled={fieldConfig.disabled}
        label={fieldConfig.label}
        placeholder={fieldConfig.placeholder}
        innerRef={fieldConfig.innerRef}
        handleSpeak={fieldConfig.handleSpeak}
        handleFocusChange={handleFocusChange}
        nextRef={nextRef}
        nextValueRef={nextValueRef}
        resetRef={resetRef}
        timer={timer}
        type={fieldConfig.type}
        setFieldValue={setFieldValue}
        getActive={getActive}
        handleActive={handleActive}
        langSelected={langSelected}
        getActiveState={getActiveState}
        onBtnRef={onBtnRef}
        skipFocusChange={skipFocusChange}
        skipBtn={translate("Skip")}
        transcript={transcript}
        listening={listening}
        voiceSelected={voiceSelected}
        resetTranscript={resetTranscript}
        required={fieldConfig.required}
        isKeyBoardInput={isKeyBoardInput}
        handleSpeakWithoutVoice={handleSpeakWithoutVoice}
        disableRef={disableRef}
        requiredBtn={translate("Required")}
      />
    );
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) {
      toast.dismiss();
      toast.warning("Your form is already being submitted. Please wait.");
      return;
    }

    setIsSubmitting(true);
    const missingFields = [];
    InputFields.forEach((field) => {
      if (field.required && !initialValues[field.name]) {
        missingFields.push(field.label);
      }
    });

    if (missingFields.length > 0) {
      setIsSubmitting(false);
      toast.dismiss();
      toast.warning(
        `Please fill in the following details: ${missingFields.join(", ")}`
      );
      return;
    }

    if (
      initialValues?.contact_number &&
      initialValues?.contact_number?.length !== 10
    ) {
      setIsSubmitting(false);
      toast.dismiss();
      toast.warning("Contact number should be 10 digits");
      return;
    }
    if (
      initialValues?.contact_email &&
      !isEmail(initialValues?.contact_email)
    ) {
      setIsSubmitting(false);
      toast.dismiss();
      toast.warning("Please enter a valid email address");
      return;
    }
    let postData = { ...initialValues };
    if (fileName) {
      postData["file"] = fileName;
    }

    postData["collab_to_udise_id"] = props.eventid.udise_id;
    postData["collab_to_school_name"] = props.eventid.school_name;
    postData["event_name"] = props.eventid.event_name;
    postData["lang"] = props.eventid.lang;
    postData["UID"] = props.eventid.UID;
    postData["collab_by_udise_id"] = props.eventid.myUDISE;
    postData["collab_by_school_name"] = props.eventid.myschoolName;

    try {
      const response = await axiosConn("post", `save-collaboration`, postData, {
        headers: { "Content-Type": "application/json" },
      });

      console.log(response,"response inside the collaboration model");

      if (response.data.status === "success") {
        toast.dismiss();
        toast.success("Collaboration Submitted Successfully!");
      } else {
        toast.dismiss();
        toast.error("Failed to submit collaboration. Please try again.");
      }
    } catch (error) {
      console.error("There was an error submitting the form!", error);
      toast.dismiss();
      toast.error("Collaboration request already sent!");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={Yup.object().shape({})}
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(false);
      }}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <Form className="bg-blue-900">
          <Modal
            {...props}
            size="lg"
            aria-labelledby="contained-modal-title-vcenter"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title id="contained-modal-title-vcenter">
                <h2>
                  {" "}
                  <span className="hdngBrkr">Collaboration</span> With School
                </h2>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="collobDivFrm collobDivFrmOm">
                <div className="row">
                  <div className="form-group col-lg-12 collobDivFrmOm_EvntNm">
                    <label htmlFor="">Event Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={props?.eventid?.event_name || ""}
                      disabled
                    />
                  </div>

                  <div className="form-group col-12">
                    <div className="row">
                      <div className="form-group col-6 pl-0">
                        <div className="row mySchlBx">
                          <div className="form-group col-12">
                            <label htmlFor="">UDISE ID (MY SCHOOL)</label>
                            <input
                              type="text"
                              className="form-control"
                              value={props.eventid.myUDISE || ""}
                              disabled
                            />
                          </div>
                          <div className="form-group col-12">
                            <label htmlFor="">School Name</label>
                            <textarea
                              disabled
                              className="form-control"
                              value={props.eventid.myschoolName || ""}
                            ></textarea>
                          </div>
                        </div>
                      </div>

                      <div className="form-group col-6 p-0">
                        <div className="row hstSchlBx">
                          <div className="form-group col-12">
                            <label htmlFor="">UDISE ID (HOST SCHOOL)</label>
                            <input
                              type="text"
                              className="form-control"
                              value={props.eventid.udise_id || "" || ""}
                              disabled
                            />
                          </div>
                          <div className="form-group col-12">
                            <label htmlFor="">School Name</label>
                            <textarea
                              disabled
                              className="form-control"
                              value={props.eventid.school_name || ""}
                            ></textarea>
                          </div>
                        </div>
                      </div>
                    </div>
                    <VirtualKeyboard lang={langSelected} />
                  </div>
                  {InputFields.slice(0, 3).map((fieldConfig) =>
                    renderInputFields(fieldConfig, setFieldValue, 6)
                  )}
                  <div className="col-6 form-group voiceformtop">
                    <label htmlFor="">Upload</label>
                    <input
                      id="file-upload"
                      className="form-control"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                    />
                  </div>
                  {InputFields.slice(3, 4).map((fieldConfig) =>
                    renderInputFields(fieldConfig, setFieldValue, 12)
                  )}
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="form-group col-12 doYuHstDv text-center">
                <input type="checkbox" onChange={() => setCheck(!check)} />
                Do you want to send this collaboration request?
              </div>

              <div className="form-group col-12 text-center">
                <Button
                  type="submit"
                  className="button green btn-login-signup loginBtnnewhm hdrBtnSrch"
                  disabled={!check || isSubmitting} // Button disabled if checkbox is not checked
                  onClick={(e) => handleSubmit(e)}
                >
                  Send Request
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        </Form>
      )}
    </Formik>
  );
}

export default CollaborationModel;
